import React, { useState, useEffect, useRef } from 'react'
import MaskedInput from 'react-text-mask'
import { useRouter } from 'next/router'

import IconCaretDown from 'Images/SVG/CaretDown.svg'
import IconError from 'Images/SVG/IconError.svg'
import { FlexDiv } from 'components/generic/HomePageComponents'
import GenericLazyLoadImage from 'components/generic/GenericLazyLoadImage'
import {
  PostalContent,
  DropdownButtonPostalCode,
  DropdownDistance,
  PostalInput,
  LocationDropdownAnchor,
  StyledDropdownAnchorContainer,
  PostalCodeErrorContainer,
} from 'components/Directories/SearchFacetsContainer/Styled'

// piping function for conformed value after mask but before display
function uppercasePipe(conformedVal) {
  return conformedVal.toUpperCase()
}

// container div to hold the text field and the button
const MainContainer = (props) => (
  <FlexDiv
    borderRadius='5px'
    border='1px #ccc solid'
    width={{ desktop: '220px' }}
    height='34px'
    mt='4px'
    {...props}
  />
)

// TODO: Refactor component into class

// Canadian dropdown uses km and American dropdown use miles
// placeholder character is ' ' and it is being piped to be all capitals
const PostalCodeInput = ({
  currentCountry,
  postalCodeUpdate,
  postalRadiusUpdate,
  invalidPostalCode,
}) => {
  // hooks to handle state of value of input, value of distance, and whether list is open
  const [openList, setOpenList] = useState(false)
  const [distance, setDistance] = useState(10)
  const [postalCode, setPostalCode] = useState('')

  const divRef = useRef(null)

  const distancePossible = [5, 10, 20, 50]

  // effect hook to handle outside clicking to hide drop down menu
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false)

    return () => {
      document.removeEventListener('click', handleClickOutside, false)
    }
  }, [])

  const router = useRouter()

  const updateFilters = () => {
    postalCodeUpdate(postalCode.replace(/\s+/g, ''))
    postalRadiusUpdate(distance)
  }

  useEffect(() => {
    setPostalCode('')
    setDistance(10)
  }, [router.query.locations])

  const handleClickOutside = (ev) => {
    if (divRef.current && !divRef.current.contains(ev.target)) {
      setOpenList(false)
    }
  }

  const handleClickOption = (distanceOption) => {
    postalRadiusUpdate(distanceOption)
    postalCodeUpdate(postalCode.replace(/\s+/g, ''))
    setOpenList(!openList)
    setDistance(distanceOption)
  }

  // determines which mask to use, based on available region
  let mask
  let countrySelected = false
  const countrySlug = currentCountry?.slug
  let placeholder = ''
  let codeKeyword
  let distanceMeasurement = 'km'

  if (currentCountry && countrySlug === 'canada') {
    mask = [/[A-Z]/i, /\d/, /[A-Z]/i, ' ', /\d/, /[A-Z]/i, /\d/]
    countrySelected = true
    placeholder = 'Postal Code'
    codeKeyword = 'postal'
  } else if (currentCountry && currentCountry.slug === 'usa') {
    mask = [/\d/, /\d/, /\d/, /\d/, /\d/]
    countrySelected = true
    placeholder = 'Zip Code'
    codeKeyword = 'zip'
    distanceMeasurement = 'mi'
  } else {
    mask = false
    codeKeyword = 'zip/postal'
  }

  const distAnchors = distancePossible.map((distanceOption, i) => (
    <StyledDropdownAnchorContainer key={i} onClick={() => handleClickOption(distanceOption)}>
      <LocationDropdownAnchor>
        within {distanceOption}
        {distanceMeasurement}
      </LocationDropdownAnchor>
    </StyledDropdownAnchorContainer>
  ))

  // checks if user has pressed enter upon typing the postal code
  const catchEnterKeyPress = (ev) => {
    if (!ev) {
      return null
    }

    const enterKeyPressed = ev.which === 13 || ev.key === 'Enter' || ev.code === 'Enter'
    if (enterKeyPressed) {
      // mask for Canadian code includes space, so it must be removed
      updateFilters()
    }
  }

  if (!countrySelected) {
    return null
  }

  return (
    <>
      <MainContainer>
        <MaskedInput
          mask={mask}
          placeholder={placeholder}
          placeholderChar={`\u2000`}
          pipe={uppercasePipe}
          padding-left='10px'
          value={postalCode}
          keepCharPositions={false}
          onChange={(ev) => setPostalCode(ev.target.value)}
          onKeyDown={(ev) => catchEnterKeyPress(ev)}
          onBlur={updateFilters}
          render={(ref, props) => (
            <PostalInput ref={ref} invalidPostalCode={invalidPostalCode} {...props} />
          )}
        />
        <DropdownButtonPostalCode
          ref={divRef}
          clickable={currentCountry}
          onClick={() => {
            currentCountry && setOpenList(!openList)
          }}
        >
          <DropdownDistance>
            within {distance}
            {distanceMeasurement}
          </DropdownDistance>
          <GenericLazyLoadImage
            src={IconCaretDown}
            alt='Down Button'
            width='16px'
            height='18px'
            mr='8px'
            style={{ float: 'right' }}
          />
          {openList && <PostalContent>{distAnchors}</PostalContent>}
        </DropdownButtonPostalCode>
      </MainContainer>
      {invalidPostalCode && (
        <PostalCodeErrorContainer>
          <GenericLazyLoadImage src={IconError} alt='Error' width='14px' height='14px' mr='8px' />
          We couldn&apos;t locate the {codeKeyword} code entered. Please verify and try again.
        </PostalCodeErrorContainer>
      )}
    </>
  )
}

export default PostalCodeInput
